/**
 * Tag Tool — Annotate > Tag panel.
 *
 * Variants (BIM):
 *   category  — Tag by Category (TG): click an element, drop its tag.
 *   all       — Tag All: one pass places tags on every taggable element of
 *               the supported categories in the model.
 *   material  — Material Tag: the tag references the material layer of the
 *               host element, not the element itself.
 *   multirebar— Multi-Rebar tag: click a line of parallel bars, one combined
 *               tag (count @ spacing) labels the whole group.
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import i18n from '../i18n';

export type TagKind = 'category' | 'all' | 'material' | 'multirebar';

export interface TagGeometryData {
    position: [number, number, number];
    targetId: string;
    /** Second anchor used by material / multi-rebar tags for the leader. */
    materialTarget?: string;
}

export interface TagElement extends Element {
    category: 'Tags';
    geometryData: TagGeometryData;
}

const KIND_LABEL: Record<TagKind, string> = {
    category: 'Tag by Category',
    all: 'Tag All Not Tagged',
    material: 'Material Tag',
    multirebar: 'Multi-Rebar Annotation Tag',
};

/** Categories that carry single-element data tags (BIM's taggable set). */
const TAGGABLE_CATEGORIES = [
    'Doors', 'Windows', 'Rooms', 'Areas', 'Walls', 'Floors', 'Ceilings',
    'Structural Columns', 'Structural Framing',
];

export class TagTool extends BaseTool {
    id = 'tag';
    name: string;
    cursor = 'text';

    private kind: TagKind;
    private hoveredElementId: string | null = null;

    constructor(kind: TagKind = 'category') {
        super();
        this.kind = kind;
        this.id = kind === 'category' ? 'tag' : `tag_${kind}`;
        this.name = KIND_LABEL[kind];
    }

    protected onActivate(): void {
        if (this.kind === 'all') {
            this.runTagAll();
            this.context?.appStore.setActiveTool('select');
            return;
        }
        this.notify(i18n.t('tools.notify.tagHover', { kind: KIND_LABEL[this.kind], target: this.kind === 'multirebar' ? i18n.t('tools.notify.targetRebarGroup') : i18n.t('tools.notify.targetElement') }));
    }

    /** Resolve a pickable element id from the pointer event (scene raycast). */
    private pickElement(event: ThreeEvent<PointerEvent>): string | null {
        return this.findElementIdAtPoint(event);
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        let targetId = this.hoveredElementId ?? this.pickElement(event);
        if (!targetId) return;

        if (this.kind === 'multirebar') {
            this.createMultiRebarTag(targetId, point);
            return;
        }
        const snapped = this.snapToGrid(point, 0.1);
        this.createTag(targetId, snapped);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const id = this.pickElement(event);
        const point = this.getIntersection(event);
        if (id && point) {
            this.hoveredElementId = id;
            this.context.appStore.setToolPreview({
                type: 'tag',
                kind: this.kind,
                position: point,
                targetId: id,
            });
        } else {
            this.hoveredElementId = null;
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerUp(): void { }

    /** Derive the displayed value from the target element's data. */
    private tagValueFor(target: Element): { value: string; parameter: string } {
        switch (target.category) {
            case 'Walls':
                return {
                    value: target.parameters['Type Name'] || target.type,
                    parameter: 'Type Name',
                };
            case 'Doors':
            case 'Windows':
                return { value: target.parameters['Mark'] || '1', parameter: 'Mark' };
            case 'Rooms':
                return {
                    value: `${target.parameters['Number'] ?? '101'}\n${target.parameters['Name'] ?? 'Room'}`,
                    parameter: 'Number',
                };
            case 'Areas':
                return { value: target.parameters['Area'] || '—', parameter: 'Area' };
            case 'Structural Columns':
            case 'Structural Framing':
                return { value: target.parameters['Mark'] || target.type, parameter: 'Mark' };
            default:
                return { value: target.type, parameter: 'Type' };
        }
    }

    private createTag(targetId: string, position: THREE.Vector3) {
        if (!this.context) return;

        const target = this.context.bimStore.project.elements.get(targetId);
        if (!target) return;

        const { value, parameter } = this.tagValueFor(target);
        let displayValue = value;
        let materialName: string | undefined;

        if (this.kind === 'material') {
            // Material tag: read the primary compound-structure layer of the
            // host. Walls/floors/ceilings store layers on geometryData as
            // { materialId }; resolve it through the project material
            // registry (id first, then name) so the tag shows the real
            // material instead of falling back to the type name.
            const project = this.context.bimStore.project;
            const layers = (target as any).geometryData?.layers;
            let layerMaterial: string | undefined;
            if (Array.isArray(layers) && layers.length) {
                const structureLayer = layers.find((l: any) => l?.function === 'Structure') ?? layers[0];
                const id = structureLayer?.materialId ?? structureLayer?.material;
                if (typeof id === 'string' && id) {
                    const direct = project.materials?.get(id);
                    if (direct?.name) layerMaterial = direct.name;
                    else if (!project.materials?.has(id)) layerMaterial = id; // legacy plain string ids ('Gypsum')
                }
            }
            materialName =
                target.parameters['Material'] ||
                target.parameters['Layer Material'] ||
                layerMaterial ||
                target.parameters['Type Name'] ||
                'Material';
            displayValue = materialName ?? value;
        }

        const geometryData: TagGeometryData = {
            position: [position.x, position.y, position.z],
            targetId,
            materialTarget: materialName ? targetId : undefined,
        };

        const tag: TagElement = {
            id: uuidv4(),
            category: 'Tags',
            type: KIND_LABEL[this.kind],
            parameters: {
                'Tag Kind': KIND_LABEL[this.kind],
                'Target Category': target.category,
                'Target Id': targetId,
                'Value': displayValue,
                'Parameter': this.kind === 'material' ? 'Material' : parameter,
            },
            constraints: [
                { type: 'HOST_CONTAINED', targetId: targetId, data: {} },
            ],
            geometryData
        };

        this.context.bimStore.addElement(tag);
    }

    /**
     * Multi-rebar: one "n @ spacing c/c" tag labeling the whole run.
     * RebarTool places a bar family as a SINGLE element whose geometryData
     * carries the resolved numberOfBars + distribution.spacing (mm), so the
     * tag reads those values (BIM's real multi-group rule) instead of
     * guessing from nearby element origins.
     */
    private createMultiRebarTag(barId: string, position: THREE.Vector3) {
        if (!this.context) return;

        const bar = this.context.bimStore.project.elements.get(barId);
        if (!bar) return;

        const geom = (bar as any).geometryData;
        if (bar.category !== 'Structural Rebar') {
            this.notify(i18n.t('tools.notify.multiRebarTagClickARebar'), 'warning');
            return;
        }
        const count = Math.max(1, Number(geom?.numberOfBars ?? geom?.quantity ?? 1) || 1);
        const spacingMm = Number(geom?.distribution?.spacing ?? geom?.spacingMm ?? 0);
        // Legacy spacing may already be meters (>0, <10): normalize to mm.
        const rawSpacing = Number(geom?.spacing ?? 0);
        const spacing = spacingMm > 0
            ? Math.round(spacingMm)
            : rawSpacing > 0
                ? Math.round(rawSpacing < 10 ? rawSpacing * 1000 : rawSpacing)
                : 200;
        const barSize = geom?.barSize ? ` ${geom.barSize}` : '';

        const geometryData: TagGeometryData = {
            position: [position.x, position.y, position.z],
            targetId: barId,
        };

        const tag: TagElement = {
            id: uuidv4(),
            category: 'Tags',
            type: KIND_LABEL.multirebar,
            parameters: {
                'Tag Kind': KIND_LABEL.multirebar,
                'Target Id': barId,
                'Value': `${count}${barSize} @ ${spacing} c/c`,
                'Bars': count,
                'Spacing': `${spacing} mm`,
            },
            constraints: [{ type: 'HOST_CONTAINED', targetId: barId, data: {} }],
            geometryData,
        };

        this.context.bimStore.addElement(tag);
        this.notify(i18n.t('tools.notify.multiRebarTag', { count: count, spacing: spacing }), 'success');
    }

    /**
     * Tag All (BIM "Tag All Not Tagged"): place category tags on every
     * taggable element that does not already carry one.
     */
    private runTagAll() {
        if (!this.context) return;
        const project = this.context.bimStore.project;

        const taggedTargets = new Set<string>();
        project.elements.forEach((el) => {
            if (el.category === 'Tags') {
                const t = el.parameters?.['Target Id'];
                if (typeof t === 'string') taggedTargets.add(t);
            }
        });

        let placed = 0;
        project.elements.forEach((el, id) => {
            if (!TAGGABLE_CATEGORIES.includes(el.category)) return;
            if (taggedTargets.has(id)) return;
            const g = (el as any).geometryData;
            const p = Array.isArray(g?.position)
                ? new THREE.Vector3(...g.position)
                : Array.isArray(g?.start) && Array.isArray(g?.end)
                    ? new THREE.Vector3(
                        (g.start[0] + g.end[0]) / 2,
                        (g.start[1] + g.end[1]) / 2,
                        g.start[2] ?? 0,
                    )
                    : null;
            if (!p) return;
            // Float the tag just above the element like BIM's auto-place.
            p.z += 0.3;
            const before = project.elements.size;
            this.createTagAuto(el, id, p);
            if (project.elements.size > before) placed++;
        });

        this.notify(
            placed > 0 ? `Tag All: ${placed} tag(s) placed.` : 'Tag All: no untagged elements found.',
            placed > 0 ? 'success' : 'info',
        );
    }

    /** Tag creation for the batch pass without hover state. */
    private createTagAuto(target: Element, targetId: string, position: THREE.Vector3) {
        if (!this.context) return;
        const { value, parameter } = this.tagValueFor(target);
        const tag: TagElement = {
            id: uuidv4(),
            category: 'Tags',
            type: KIND_LABEL.category,
            parameters: {
                'Tag Kind': KIND_LABEL.category,
                'Target Category': target.category,
                'Target Id': targetId,
                'Value': value,
                'Parameter': parameter,
            },
            constraints: [{ type: 'HOST_CONTAINED', targetId, data: {} }],
            geometryData: { position: [position.x, position.y, position.z], targetId },
        };
        this.context.bimStore.addElement(tag);
    }
}
